import { decode } from 'cbor2'
import { u8ToHex } from './utils'

type Resolver = (data: any) => void
export type NotificationsListener = (
  messageType: number,
  data: Map<number, any>,
) => void

export interface WebSocketDispatcher {
  send<T>(messageId: Uint8Array, data: Uint8Array): Promise<T>
  setNotificationsListener(listener: NotificationsListener): void
  removeNotificationsListener(): void
  close(): void
}

export function createWebSocketDispatcher(
  socket: WebSocket,
): WebSocketDispatcher {
  const pending = new Map<string, Resolver>()
  let notificationsListener: NotificationsListener | null = null

  const handleMessage = async (event: MessageEvent) => {
    const buffer = await getDataBuffer(event.data)
    if (!buffer) return

    try {
      const decoded = decode<Map<number, any>>(buffer)
      const messageHeader = decoded.get(0) as Map<number, any>
      let messageId = u8ToHex(messageHeader.get(3) as Uint8Array)
      if (pending.has(messageId)) {
        const resolver = pending.get(messageId)!
        pending.delete(messageId)
        resolver(decoded)
      } else {
        const messageType = messageHeader.get(2) as number
        notificationsListener?.(messageType, decoded)
      }
    } catch (err) {
      console.error('CBOR decode failed:', err)
    }
  }

  socket.addEventListener('message', handleMessage)

  const send = <T>(messageId: Uint8Array, data: Uint8Array): Promise<T> => {
    console.log('send message')
    return new Promise((resolve, reject) => {
      if (socket.readyState !== WebSocket.OPEN) {
        return reject(new Error('WebSocket is not open'))
      }
      const sendMessageId = u8ToHex(messageId)
      pending.set(sendMessageId, response => resolve(response as T))
      try {
        socket.send(data)
      } catch (err) {
        console.log('error sending message', err)
        pending.delete(sendMessageId)
        reject(err)
      }
    })
  }

  const setNotificationsListener = (listener: NotificationsListener) => {
    notificationsListener = listener
  }

  const removeNotificationsListener = () => {
    notificationsListener = null
  }

  const close = () => {
    socket.removeEventListener('message', handleMessage)
    pending.clear()
  }

  return {
    send,
    setNotificationsListener,
    removeNotificationsListener,
    close,
  }
}

async function getDataBuffer(
  data: Blob | ArrayBuffer,
): Promise<Uint8Array | null> {
  if (data instanceof ArrayBuffer) {
    return new Uint8Array(data)
  }
  if (data instanceof Blob) {
    const arrayBuffer = await data.arrayBuffer()
    return new Uint8Array(arrayBuffer)
  }
  return null
}
